import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import {
    CheckCircleIcon,
    XCircleIcon,
    CalendarIcon,
    UserGroupIcon,
    MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

const ConfirmModal = ({ isOpen, onClose, onConfirm, title, message, loading }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#18216D]/20 backdrop-blur-md animate-in fade-in duration-300">
            <div className="bg-white rounded-[2.5rem] p-8 md:p-12 shadow-2xl shadow-indigo-900/20 max-w-md w-full border border-slate-100 animate-in zoom-in-95 duration-300 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#FFC425]/5 rounded-full -mr-16 -mt-16"></div>
                
                <div className="relative z-10 text-center space-y-6">
                    <div className="w-20 h-20 bg-[#FFC425]/10 rounded-3xl mx-auto flex items-center justify-center text-[#18216D]">
                        <CheckCircleIcon className="w-10 h-10" />
                    </div>
                    
                    <div className="space-y-2">
                        <h3 className="text-2xl font-black text-[#18216D] tracking-tight italic">{title}</h3>
                        <p className="text-slate-400 font-bold text-sm leading-relaxed">{message}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4 pt-4">
                        <button
                            onClick={onClose}
                            className="px-6 py-4 bg-slate-50 text-slate-400 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-100 transition-all"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={onConfirm}
                            disabled={loading}
                            className="px-6 py-4 bg-[#18216D] text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-[#1e2a8a] transition-all shadow-lg shadow-indigo-900/20 flex items-center justify-center gap-2"
                        >
                            {loading ? (
                                <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                            ) : (
                                "Confirm"
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const AttendanceRegistry = ({ 
    courseId, 
    students = [], 
    title = "Daily Attendance",
    subtitle = "Record and track student presence",
    apiUrl = "/teachers/api/students/attendance/"
}) => {
    const { post, get } = useApi();
    const { showToast } = useAppState();
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [attendanceData, setAttendanceData] = useState({});
    const [searchQuery, setSearchQuery] = useState('');
    const [saving, setSaving] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);

    const fetchAttendance = useCallback(async () => {
        try {
            const response = await get(`${apiUrl}?date=${date}&learning_area_id=${courseId}`);
            const mapped = {};
            response.forEach(rec => {
                mapped[rec.student_id] = rec.status;
            });
            setAttendanceData(mapped);
        } catch (error) {
            console.error('Error fetching attendance:', error);
        }
    }, [get, apiUrl, date, courseId]);

    useEffect(() => {
        fetchAttendance();
    }, [fetchAttendance]);

    const markAttendance = async (studentId, status) => {
        try {
            setSaving(studentId);
            const formData = new FormData();
            formData.append('student_id', studentId);
            formData.append('date', date);
            formData.append('status', status);
            formData.append('learning_area_id', courseId);

            await post(apiUrl, formData);
            setAttendanceData(prev => ({ ...prev, [studentId]: status }));
            showToast('Attendance recorded', 'success');
        } catch (error) {
            console.error('Error marking attendance:', error);
            showToast('Failed to record attendance', 'error');
        } finally {
            setSaving(false);
        }
    };

    const markAllAsPresent = async () => {
        try {
            setSaving('bulk');
            const formData = new FormData();
            formData.append('bulk_mark', 'true');
            formData.append('date', date);
            formData.append('status', 'Present');
            formData.append('learning_area_id', courseId);

            await post(apiUrl, formData);
            
            // Optimistically update all
            const updated = { ...attendanceData };
            students.forEach(s => { updated[s.id] = 'Present'; });
            setAttendanceData(updated);
            
            showToast('All students marked as Present', 'success');
            setIsConfirmOpen(false);
        } catch (error) {
            console.error('Error in bulk marking:', error);
            showToast('Failed to bulk mark attendance', 'error');
        } finally {
            setSaving(false);
        }
    };

    const filteredStudents = students.filter(student =>
        student.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        student.student_id?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="space-y-6">
            <ConfirmModal 
                isOpen={isConfirmOpen}
                onClose={() => setIsConfirmOpen(false)}
                onConfirm={markAllAsPresent}
                loading={saving === 'bulk'}
                title="Bulk Attendance"
                message="Are you sure you want to mark all enrolled students as Present for this date?"
            />
            
            {/* Desktop Header */}
            <div className="hidden md:flex flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-3xl border border-slate-100 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-50/5 rounded-full -mr-16 -mt-16 blur-2xl"></div>
                <div className="relative z-10">
                    <h2 className="text-2xl font-black text-[#18216D] tracking-tight">{title}</h2>
                    <p className="text-slate-400 text-sm font-medium">{subtitle}</p>
                </div>
                <div className="relative z-10 flex flex-row items-center gap-4">
                    <div className="relative w-64">
                        <MagnifyingGlassIcon className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Search students..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-12 pr-6 py-3 bg-slate-50 border-none rounded-2xl font-black text-xs text-[#18216D] focus:ring-2 focus:ring-[#18216D]/20 transition-all placeholder:text-slate-300"
                        />
                    </div>
                    <div className="relative">
                        <CalendarIcon className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            className="pl-12 pr-6 py-3 bg-slate-50 border-none rounded-2xl font-black text-xs text-[#18216D] focus:ring-2 focus:ring-[#18216D]/20 transition-all uppercase tracking-widest"
                        />
                    </div>
                    <button
                        onClick={() => setIsConfirmOpen(true)}
                        disabled={saving === 'bulk' || filteredStudents.length === 0}
                        className="flex items-center gap-2 px-6 py-3 bg-[#FFC425] text-[#18216D] rounded-2xl font-black text-xs hover:bg-[#ffcd45] transition-all shadow-lg shadow-amber-500/20 disabled:opacity-50 active:scale-95"
                    >
                        <CheckCircleIcon className="w-5 h-5" />
                        <span>Mark All Present</span>
                    </button>
                </div>
            </div>

            {/* Mobile Header */}
            <div className="md:hidden space-y-6">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="h-1 w-8 bg-[#FFC425] rounded-full"></div>
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Registry Control</p>
                    </div>
                    <h1 className="text-4xl font-black text-[#18216D] tracking-tighter">{title}</h1>
                </div>

                <div className="flex flex-col gap-4">
                    <div className="relative w-full">
                        <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                        <input
                            type="text"
                            placeholder="Search scholars..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-12 pr-4 py-4 bg-white border border-slate-100 rounded-2xl shadow-sm text-sm font-bold text-[#18216D]"
                        />
                    </div>
                    
                    <div className="flex items-center gap-3">
                        <div className="relative flex-1">
                            <CalendarIcon className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                type="date"
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                className="w-full pl-12 pr-4 py-4 bg-white border border-slate-100 rounded-2xl shadow-sm text-xs font-black text-[#18216D] uppercase"
                            />
                        </div>
                        <button
                            onClick={() => setIsConfirmOpen(true)}
                            disabled={saving === 'bulk' || filteredStudents.length === 0}
                            className="p-4 bg-[#FFC425] text-[#18216D] rounded-2xl shadow-lg shadow-amber-900/10 active:scale-95 transition-all"
                        >
                            <CheckCircleIcon className="w-6 h-6 stroke-[2]" />
                        </button>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-xl shadow-indigo-900/5 overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="bg-slate-50 border-b border-slate-100 text-left">
                            <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Student Name</th>
                            <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Admission No</th>
                            <th className="px-8 py-5 text-center text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Status Control</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                        {filteredStudents.map((student) => (
                            <tr key={student.id} className="hover:bg-indigo-50/20 transition-all group">
                                <td className="px-8 py-6">
                                    <div className="flex items-center gap-4">
                                        <div className="w-10 h-10 rounded-xl bg-[#18216D]/5 flex items-center justify-center text-[#18216D] font-black border border-[#18216D]/10 text-xs">
                                            {student.name?.split(' ').map(n => n[0]).join('')}
                                        </div>
                                        <div>
                                            <p className="font-black text-[#18216D] tracking-tight">{student.name}</p>
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Enrolled Student</p>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-8 py-6">
                                    <span className="text-sm font-black text-slate-500">{student.student_id}</span>
                                </td>
                                <td className="px-8 py-6">
                                    <div className="flex items-center justify-center gap-2">
                                        {[
                                            { id: 'Present', icon: CheckCircleIcon, color: 'emerald', label: 'Present' },
                                            { id: 'Absent', icon: XCircleIcon, color: 'rose', label: 'Absent' }
                                        ].map((status) => {
                                            const isActive = attendanceData[student.id] === status.id;
                                            return (
                                                <button
                                                    key={status.id}
                                                    disabled={saving === student.id}
                                                    onClick={() => markAttendance(student.id, status.id)}
                                                    className={`
                                                        px-4 py-2 rounded-xl flex items-center gap-2 transition-all border-2
                                                        ${isActive
                                                            ? `bg-${status.color}-50 border-${status.color}-200 text-${status.color}-600 shadow-sm`
                                                            : 'bg-white border-transparent text-slate-300 hover:bg-slate-50 hover:text-slate-500'}
                                                        ${saving === student.id ? 'opacity-50 cursor-not-allowed' : ''}
                                                    `}
                                                    title={status.label}
                                                >
                                                    <status.icon className={`w-5 h-5 ${saving === student.id ? 'animate-pulse' : ''}`} />
                                                    <span className="text-[10px] font-black uppercase tracking-widest">{status.label}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {filteredStudents.length === 0 && (
                    <div className="p-20 text-center">
                        <UserGroupIcon className="w-12 h-12 text-slate-200 mx-auto mb-4" />
                        <p className="text-slate-400 font-bold italic tracking-tight">
                            {searchQuery ? `No students matching "${searchQuery}"` : 'No students enrolled in this learning area.'}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AttendanceRegistry;
